/**
 * Централизованный менеджер шрифтов
 * Решает проблемы:
 * - Дублирование загрузки одного и того же шрифта
 * - Race conditions при экспорте
 * - Кеширование загруженных шрифтов
 */

interface LoadedFont {
  fontFamily: string;
  fontFace: FontFace;
  url: string;
}

class FontManager {
  private loadedFonts: Map<string, LoadedFont> = new Map();
  private loadingPromises: Map<string, Promise<LoadedFont>> = new Map();

  /**
   * Генерирует стабильное имя для шрифта на основе URL
   * (без Date.now() для избежания дубликатов)
   */
  private generateFontFamily(fontName: string, url: string): string {
    // Используем hash URL для уникальности
    const urlHash = url.split('').reduce((hash, char) => {
      return ((hash << 5) - hash) + char.charCodeAt(0);
    }, 0);
    
    return `CustomFont_${fontName}_${Math.abs(urlHash)}`;
  }

  /**
   * Загружает шрифт и кеширует его
   * Если шрифт уже загружен - возвращает из кеша
   */
  async loadFont(url: string, fontName: string): Promise<LoadedFont> {
    // Проверяем кеш
    if (this.loadedFonts.has(url)) {
      console.log('✅ Шрифт уже загружен из кеша:', fontName);
      return this.loadedFonts.get(url)!;
    }

    // Проверяем, не загружается ли уже
    if (this.loadingPromises.has(url)) {
      console.log('⏳ Шрифт загружается, ждём...:', fontName);
      return this.loadingPromises.get(url)!;
    }

    // Начинаем загрузку
    console.log('🔄 Загружаем новый шрифт:', fontName);
    
    const loadPromise = this._loadFontInternal(url, fontName);
    this.loadingPromises.set(url, loadPromise);

    try {
      const loadedFont = await loadPromise;
      this.loadedFonts.set(url, loadedFont);
      return loadedFont;
    } finally {
      this.loadingPromises.delete(url);
    }
  }

  /**
   * Внутренний метод для загрузки шрифта
   */
  private async _loadFontInternal(url: string, fontName: string): Promise<LoadedFont> {
    const fontFamily = this.generateFontFamily(fontName, url);
    
    // Проверяем, может шрифт уже загружен в document.fonts
    const existingFont = Array.from(document.fonts).find(
      f => f.family === fontFamily
    );

    if (existingFont && existingFont.status === 'loaded') {
      console.log('✅ Шрифт уже в document.fonts:', fontFamily);
      return { fontFamily, fontFace: existingFont, url };
    }

    // Загружаем шрифт с широким диапазоном weight для поддержки variable fonts
    const fontFace = new FontFace(fontFamily, `url(${url})`, {
      weight: '1 1000',
      style: 'normal',
      stretch: '25% 200%',
    });
    
    try {
      const loadedFontFace = await fontFace.load();
      document.fonts.add(loadedFontFace);
      console.log('✅ Шрифт успешно загружен:', fontFamily);
      
      return { fontFamily, fontFace: loadedFontFace, url };
    } catch (error) {
      console.error('❌ Ошибка загрузки шрифта:', fontName, error);
      throw error;
    }
  }

  /**
   * Гарантирует что шрифт загружен перед использованием
   * (для использования в экспорте)
   */
  async ensureFontLoaded(url: string, fontName: string): Promise<string> {
    if (!url || url === '') {
      return 'Arial'; // fallback
    }

    try {
      const loadedFont = await this.loadFont(url, fontName);
      return loadedFont.fontFamily;
    } catch (error) {
      console.error('❌ Не удалось загрузить шрифт, используем Arial:', error);
      return 'Arial';
    }
  }

  /**
   * Получает fontFamily для уже загруженного шрифта
   * Если не загружен - возвращает null
   */
  getFontFamily(url: string): string | null {
    const loadedFont = this.loadedFonts.get(url);
    return loadedFont ? loadedFont.fontFamily : null;
  }

  /**
   * Предзагрузка массива шрифтов
   * (полезно для загрузки всех шрифтов из списка при старте)
   */
  async preloadFonts(fonts: Array<{ url: string; name: string }>): Promise<void> {
    console.log('🚀 Предзагрузка шрифтов:', fonts.length);
    
    const loadPromises = fonts.map(font => 
      this.loadFont(font.url, font.name).catch(err => {
        console.warn('⚠️ Не удалось предзагрузить шрифт:', font.name, err);
        return null;
      })
    );

    await Promise.all(loadPromises);
    console.log('✅ Предзагрузка шрифтов завершена');
  }

  /**
   * Очищает кеш (если нужно)
   */
  clearCache(): void {
    this.loadedFonts.clear();
    this.loadingPromises.clear();
    console.log('🧹 Кеш шрифтов очищен');
  }

  /**
   * Получает статистику
   */
  getStats() {
    return {
      loaded: this.loadedFonts.size,
      loading: this.loadingPromises.size,
      fonts: Array.from(this.loadedFonts.keys())
    };
  }
}

// Singleton instance
export const fontManager = new FontManager();